import React, { useEffect, useRef } from "react";
import { StyleSheet, View, Animated, Easing, type StyleProp, type ViewStyle } from "react-native";
import { useComponentTheme } from "@/hooks/useComponentTheme";

export type ThemedProgressBarProps = {
  /**
   * Whether the bar is in active indeterminate animation mode.
   * If `progress` is provided, `loading` can be omitted.
   */
  loading?: boolean;
  /**
   * Optional custom highlight color overriding the theme fill.
   */
  color?: string;
  /**
   * Optional custom track background color overriding the theme track.
   */
  trackColor?: string;
  /**
   * When true (default), the bar renders as a zero-height absolute overlay pinned
   * to the top of its nearest `position: relative` ancestor, causing zero layout shift.
   * Pass `overlay={false}` only when the bar is inside an in-flow container.
   */
  overlay?: boolean;
  /**
   * Optional determinate progress percentage (0 to 100).
   * When provided, renders a fixed percentage fill instead of infinite indeterminate animation.
   */
  progress?: number;
  /**
   * Optional container height override.
   */
  height?: number;
  /**
   * Optional style override.
   */
  style?: StyleProp<ViewStyle>;
  /**
   * Optional test identifier.
   */
  testID?: string;
};

/**
 * A reusable, premium, animated horizontal linear progress bar component.
 * Standardized across the platform with zero layout shifts via useComponentTheme().progressBar.
 *
 * Supports both:
 * 1. Indeterminate animated loading mode (`loading={true}`).
 * 2. Determinate progress percentage mode (`progress={75}`).
 *
 * @param props ThemedProgressBarProps
 */
export function ThemedProgressBar({
  loading,
  color,
  trackColor: customTrackColor,
  overlay = true,
  progress,
  height,
  style,
  testID,
}: ThemedProgressBarProps) {
  const { progressBar } = useComponentTheme();
  const themeLinear = progressBar.linear;

  const isDeterminate = progress !== undefined && progress !== null;
  const isActive = isDeterminate ? progress > 0 : Boolean(loading);

  const tintColor = color || themeLinear.fillColor;
  const trackColor = customTrackColor || themeLinear.trackColor;
  const barHeight = height || themeLinear.height;

  const animValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (loading === true && !isDeterminate) {
      const loop = Animated.loop(
        Animated.timing(animValue, {
          toValue: 1,
          duration: themeLinear.animationDuration,
          easing: Easing.linear,
          useNativeDriver: false,
        })
      );
      loop.start();
      return () => {
        loop.stop();
      };
    } else {
      animValue.setValue(0);
    }
  }, [loading, isDeterminate, animValue, themeLinear.animationDuration]);

  if (!isActive) return null;

  const leftPosition = animValue.interpolate({
    inputRange: [0, 1],
    outputRange: ["-40%", "100%"],
  });

  const containerStyle: StyleProp<ViewStyle> = [
    overlay ? styles.overlayContainer : styles.inFlowContainer,
    {
      backgroundColor: trackColor,
      height: barHeight,
      zIndex: themeLinear.zIndex,
    },
    style,
  ];

  return (
    <View style={containerStyle} testID={testID} accessibilityRole="progressbar">
      {isDeterminate ? (
        <View
          style={[
            styles.determinateFill,
            {
              backgroundColor: tintColor,
              width: `${Math.max(0, Math.min(100, progress!))}%`,
            },
          ]}
        />
      ) : (
        <Animated.View
          style={[
            styles.fill,
            {
              backgroundColor: tintColor,
              left: leftPosition,
            },
          ]}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  overlayContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    overflow: "hidden",
    zIndex: 20,
    pointerEvents: "none",
  } as any,
  inFlowContainer: {
    width: "100%",
    height: 3,
    overflow: "hidden",
    position: "relative",
    borderRadius: 2,
  },
  fill: {
    height: "100%",
    width: "40%",
    position: "absolute",
    borderRadius: 2,
  },
  determinateFill: {
    height: "100%",
    borderRadius: 2,
  },
});
